/**
 * Document source resolution helpers.
 *
 * Determines the human-readable origin of a finance document (expense/DIN)
 * based on the `work_items.source` column and `custom_fields.source` /
 * `custom_fields.source_module` values set by the various intake paths:
 *
 * - Email          → documentIntake.js (api_websocket_v2) sets `source: 'email'`
 * - WhatsApp       → document-intake-handler.js (whatsapp_client_api) sets `source: 'whatsapp'`
 * - Smart Task     → ExpensesManagementModal.tsx → useCaptureSession.ts sets `custom_fields.source: 'smarttask'`
 * - Smart Automation → SmartAutomation.tsx sets `custom_fields.source: 'smart-automation'`
 *
 * The `custom_fields.source` check takes priority so that Smart Task / Smart
 * Automation documents (which flow through the expense-intake API with
 * `source: 'internal'`) are correctly identified.
 */

export type DocumentSourceKey =
  | 'email'
  | 'whatsapp'
  | 'smart_task'
  | 'smart_automation'
  | 'smart_link'
  | 'quickbooks'
  | 'sage'
  | 'xero'
  | 'dynamics365'
  | 'sage_intacct'
  | 'total_access'
  | 'external';

export interface DocumentSourceMeta {
  key: DocumentSourceKey;
  label: string;
  /** Tailwind classes for a small pill/badge. */
  badgeClassName: string;
  /** Lucide icon name (consumers map to a component). */
  icon: 'mail' | 'message-circle' | 'check-square' | 'zap' | 'link' | 'cloud' | 'globe';
}

const SOURCE_META: Record<DocumentSourceKey, DocumentSourceMeta> = {
  email: {
    key: 'email',
    label: 'Email',
    badgeClassName:
      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/40',
    icon: 'mail',
  },
  whatsapp: {
    key: 'whatsapp',
    label: 'WhatsApp',
    badgeClassName:
      'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40',
    icon: 'message-circle',
  },
  smart_task: {
    key: 'smart_task',
    label: 'Smart Task',
    badgeClassName:
      'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/40',
    icon: 'check-square',
  },
  smart_automation: {
    key: 'smart_automation',
    label: 'Smart Automation',
    badgeClassName:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/40',
    icon: 'zap',
  },
  smart_link: {
    key: 'smart_link',
    label: 'Smart-Link',
    badgeClassName:
      'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800/40',
    icon: 'link',
  },
  quickbooks: {
    key: 'quickbooks',
    label: 'QuickBooks Online',
    badgeClassName:
      'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40',
    icon: 'cloud',
  },
  sage: {
    key: 'sage',
    label: 'Sage Business Cloud',
    badgeClassName:
      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/40',
    icon: 'cloud',
  },
  xero: {
    key: 'xero',
    label: 'Xero',
    badgeClassName:
      'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800/40',
    icon: 'cloud',
  },
  dynamics365: {
    key: 'dynamics365',
    label: 'Dynamics 365',
    badgeClassName:
      'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/40',
    icon: 'cloud',
  },
  sage_intacct: {
    key: 'sage_intacct',
    label: 'Sage Intacct',
    badgeClassName:
      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/40',
    icon: 'cloud',
  },
  total_access: {
    key: 'total_access',
    label: 'Total Access',
    badgeClassName:
      'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-700/40 dark:text-slate-200 dark:border-slate-600/40',
    icon: 'globe',
  },
  // Generic fallback for external take-on rows where the adapter tag is
  // missing (synced_from unset) — only produced by resolveDocumentOrigin.
  external: {
    key: 'external',
    label: 'External System',
    badgeClassName:
      'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-700/40',
    icon: 'cloud',
  },
};

/**
 * Resolve the document source from the `source` column and `custom_fields`.
 *
 * @param sourceCol   - The raw `work_items.source` value (e.g. 'email', 'whatsapp', 'internal', 'manual').
 * @param customFields - The parsed `custom_fields` JSON object (may be null/undefined).
 * @returns The resolved source metadata, or `null` when no recognised source is found.
 */
export function resolveDocumentSource(
  sourceCol: string | null | undefined,
  customFields: Record<string, unknown> | null | undefined,
): DocumentSourceMeta | null {
  if (customFields && typeof customFields === 'object') {
    const cfSource = customFields.source;
    const cfSourceModule = customFields.source_module;
    const cfSyncedFrom = customFields.synced_from;

    if (cfSource === 'smarttask' || cfSourceModule === 'smart_task') {
      return SOURCE_META.smart_task;
    }
    if (cfSource === 'smart-automation' || cfSourceModule === 'smart_automation') {
      return SOURCE_META.smart_automation;
    }

    // External accounting system sources (QBO, Sage, Xero, Dynamics 365)
    // The sync service sets custom_fields.synced_from to the adapter ID
    // (e.g. 'quickbooks', 'sage', 'xero', 'dynamics365').
    if (typeof cfSyncedFrom === 'string' && cfSyncedFrom in SOURCE_META) {
      return SOURCE_META[cfSyncedFrom as DocumentSourceKey];
    }
  }

  // External take-on: source column is 'external_take_on' — the specific
  // adapter is normally resolved from custom_fields.synced_from above, but
  // when that tag is missing fall back to a generic external badge rather
  // than mislabelling the document as created in Total Access.
  if (sourceCol === 'external_take_on') {
    return SOURCE_META.external;
  }
  if (sourceCol === 'email') {
    return SOURCE_META.email;
  }
  if (sourceCol === 'whatsapp') {
    return SOURCE_META.whatsapp;
  }
  if (sourceCol === 'smart_link') {
    return SOURCE_META.smart_link;
  }

  return null;
}

/**
 * Display label for an external accounting system id — the
 * `work_items.external_system` column / adapter id ('quickbooks', 'xero',
 * 'sage', 'sage-za', 'dynamics365', 'sage_intacct'). Used when labelling
 * documents that were published OUT to the external system (as opposed to
 * resolveDocumentSource, which labels the intake channel).
 */
export function externalSystemLabel(systemId: string | null | undefined): string {
  if (!systemId) return 'external system';
  const normalized = systemId === 'sage-za' ? 'sage' : systemId;
  return SOURCE_META[normalized as DocumentSourceKey]?.label ?? systemId;
}

/**
 * Convenience wrapper that accepts a work item / document object with
 * `source` and `custom_fields` properties.
 */
export function resolveDocumentSourceFromItem(
  item: { source?: string | null; custom_fields?: Record<string, unknown> | null } | null | undefined,
): DocumentSourceMeta | null {
  if (!item) return null;
  return resolveDocumentSource(item.source ?? null, item.custom_fields ?? null);
}

/**
 * Resolve the document's origin — like {@link resolveDocumentSource}, but
 * falls back to 'Total Access' when no intake channel / external system is
 * recognised. Anything not explicitly sourced was created inside the
 * platform (manual capture, DIN materialise, conversions), so the result is
 * never null. Use this where the UI wants to label provenance on every
 * document (e.g. the DocumentModal header badge, list subtitles).
 */
export function resolveDocumentOrigin(
  sourceCol: string | null | undefined,
  customFields: Record<string, unknown> | null | undefined,
): DocumentSourceMeta {
  return resolveDocumentSource(sourceCol, customFields) ?? SOURCE_META.total_access;
}
